import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { errMsg } from "@/lib/errors";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

export default function Business() {
  const [b, setB] = useState(null);
  const [industrySel, setIndustrySel] = useState("custom");
  const [switching, setSwitching] = useState(false);
  useEffect(() => { api.get("/business").then(r => { setB(r.data); setIndustrySel(r.data.industry || "custom"); }); }, []);
  if (!b) return <div className="p-8 text-slate-500">Loading…</div>;

  const INDUSTRY_OPTIONS = [
    ["barbershop", "Barbershop"], ["salon_spa", "Salon / Spa / Nails"], ["dental", "Dental"],
    ["law", "Law"], ["home_services", "Home services / Plumbing"], ["restaurant", "Restaurant"],
    ["auto_repair", "Auto repair"], ["custom", "Custom / Other"],
  ];
  const applyIndustry = async () => {
    setSwitching(true);
    try {
      const r = await api.post("/demo/set-industry", { industry: industrySel });
      setB(r.data.business);
      const label = INDUSTRY_OPTIONS.find(([k]) => k === industrySel)?.[1] || industrySel;
      toast.success(`Demo switched to ${label} — the next call uses it. Same number.`);
    } catch (e) { toast.error(errMsg(e, "Switch failed")); }
    finally { setSwitching(false); }
  };

  const set = (k, v) => setB({ ...b, [k]: v });
  const save = async () => {
    try {
      await api.patch("/business", {
        name: b.name, description: b.description, address: b.address, phone: b.phone,
        website: b.website, email: b.email, google_review_url: b.google_review_url,
        timezone: b.timezone,
        onboarding: { ...b.onboarding, business: true },
      });
      toast.success("Business info saved");
    } catch { toast.error("Save failed"); }
  };

  return (
    <div>
      <h1 className="text-3xl font-bold tracking-tight">Business</h1>
      <p className="text-slate-600 mt-1">The core info ASFA uses on every call.</p>

      {b.demo && (
        <div className="mt-8 bg-amber-50 border border-amber-200 rounded-2xl p-6 max-w-3xl" data-testid="industry-switcher">
          <div className="font-semibold text-sm text-amber-900">Demo industry switcher</div>
          <p className="text-xs text-amber-800 mt-1">Testing only — flips this demo business's industry, sample services, greeting and AI playbook instantly. Same phone number; the next call uses the new industry. Not available on real customer accounts.</p>
          <div className="flex items-center gap-2 mt-3 flex-wrap">
            <select data-testid="industry-select" value={industrySel} onChange={(e) => setIndustrySel(e.target.value)}
              className="h-10 rounded-xl border border-amber-300 px-3 text-sm bg-white">
              {INDUSTRY_OPTIONS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
            <Button data-testid="industry-apply-btn" onClick={applyIndustry} disabled={switching} className="rounded-xl bg-amber-600 hover:bg-amber-700">
              {switching ? "Switching…" : "Switch industry"}
            </Button>
          </div>
        </div>
      )}

      <div className="mt-8 bg-white border border-slate-200 rounded-2xl p-6 max-w-3xl">
        <div className="grid sm:grid-cols-2 gap-4">
          <div><Label>Business name</Label><Input data-testid="biz-name" value={b.name || ""} onChange={(e) => set("name", e.target.value)} className="mt-1.5" /></div>
          <div><Label>Phone</Label><Input data-testid="biz-phone" value={b.phone || ""} onChange={(e) => set("phone", e.target.value)} className="mt-1.5" /></div>
          <div className="sm:col-span-2"><Label>Address</Label><Input data-testid="biz-address" value={b.address || ""} onChange={(e) => set("address", e.target.value)} className="mt-1.5" /></div>
          <div><Label>Website</Label><Input data-testid="biz-website" value={b.website || ""} onChange={(e) => set("website", e.target.value)} className="mt-1.5" /></div>
          <div><Label>Email</Label><Input data-testid="biz-email" value={b.email || ""} onChange={(e) => set("email", e.target.value)} className="mt-1.5" /></div>
          <div className="sm:col-span-2">
            <Label>Google review link</Label>
            <Input data-testid="biz-google-review" value={b.google_review_url || ""} onChange={(e) => set("google_review_url", e.target.value)} className="mt-1.5"
              placeholder="https://g.page/r/your-business/review" />
            <p className="text-xs text-slate-500 mt-1">Texted to customers 30 minutes after their appointment. Find yours in Google Business Profile → Ask for reviews.</p>
          </div>
          <div>
            <Label>Business timezone</Label>
            <select data-testid="biz-timezone" value={b.timezone || "America/Toronto"} onChange={(e) => set("timezone", e.target.value)}
              className="mt-1.5 w-full h-10 rounded-xl border border-slate-200 px-3 text-sm bg-white">
              {["America/Toronto", "America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles", "America/Vancouver", "America/Edmonton", "America/Winnipeg", "America/Halifax", "America/Phoenix", "America/Anchorage", "Pacific/Honolulu", "Europe/London"].map(tz => <option key={tz} value={tz}>{tz}</option>)}
            </select>
            <p className="text-xs text-slate-500 mt-1">All appointment times are interpreted and confirmed in this timezone.</p>
          </div>
          <div className="sm:col-span-2"><Label>Description</Label><Textarea data-testid="biz-desc" rows={3} value={b.description || ""} onChange={(e) => set("description", e.target.value)} className="mt-1.5" /></div>
        </div>
        <Button data-testid="biz-save-btn" onClick={save} className="mt-6 rounded-xl bg-blue-600 hover:bg-blue-700">Save</Button>
      </div>
    </div>
  );
}
